import { observable, computed } from 'mobx'
import { unixEpoch } from '@/utils/date-util'
import { Id } from '@/infra/id'
import { Member } from '@/features/iam/members/Member'
import { Model, type ModelProps } from '@/infra/model'
import { links } from '@/utils/link'
import { stripMinidown } from '@/minidown/strip'

export type NotificationProps = ModelProps<Notification>

/**
 * Notification
 */
export class Notification extends Model<Notification> {
  id = Id.none
  key = ''

  @observable accessor message = ''
  @observable accessor dateDispatch: Date = unixEpoch()
  @observable accessor image: NotificationImage = { type: 'None' }
  @observable accessor action: NotificationAction = { type: 'None' }
  @observable accessor recipient = Member.none
  @observable accessor status: NotificationStatus = 'Sent'

  /**
   * Image URL to show for the notification, if any.
   */
  @computed
  get imageUrl() {
    const defaultImage = this.recipient.workspace.branding.logos.icon
    const image = this.image
    if (!image) {
      return defaultImage
    }

    switch (image.type) {
      case 'Static':
        return image.url
      case 'UserAvatar':
        return image.member.avatar || image.member.avatarFallback
      default:
        return defaultImage
    }
  }

  /**
   * URL to navigate to when the notification is interacted with.
   */
  @computed
  get actionUrl() {
    const action = this.action
    if (!action) {
      return null
    }

    switch (action.type) {
      case 'ViewAvailableJobs':
        return links.opportunities()
      case 'ViewJob':
        return links.job(action.jobId)
      default:
        return null
    }
  }

  /**
   * The notification text without any formatting.
   */
  @computed
  get plainText() {
    return stripMinidown(this.message)
  }
}

export interface NoImage {
  type: 'None'
}

export interface StaticImage {
  type: 'Static'
  url: string
}

export interface UserAvatarImage {
  type: 'UserAvatar'
  member: Member
}

/**
 * The image a notification shows.
 */
export declare type NotificationImage = NoImage | StaticImage | UserAvatarImage

export interface DoNothingAction {
  type: 'None'
}

export interface ViewAvailableJobsAction {
  type: 'ViewAvailableJobs'
}

export interface ViewJobAction {
  type: 'ViewJob'
  jobId: string
}

/**
 * What interacting with a notification does.
 */
export declare type NotificationAction =
  DoNothingAction | ViewJobAction | ViewAvailableJobsAction

/**
 * Whether a notification was sent and read.
 */
export type NotificationStatus = 'Queued' | 'Sent' | 'Read'
